import { useEffect, useRef } from "react";
import VimeoPlayer from "@vimeo/player";
import { parseVideo } from "@/lib/api";

let ytPromise;
const loadYT = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!ytPromise) {
    ytPromise = new Promise((res) => {
      window.onYouTubeIframeAPIReady = () => res(window.YT);
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    });
  }
  return ytPromise;
};

// Exposes a uniform probe: { playing, position, duration }
export const TrackedPlayer = ({ src, probeRef }) => {
  const box = useRef(null);
  const vid = useRef(null);
  const info = parseVideo(src);

  useEffect(() => {
    let player;
    const state = { playing: false, ended: false, position: 0, duration: 0 };
    probeRef.current = () => state;

    if (info.kind === "youtube") {
      const el = document.createElement("div");
      box.current.appendChild(el);
      loadYT().then((YT) => {
        player = new YT.Player(el, {
          videoId: info.id, width: "100%", height: "100%",
          playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
          events: { onStateChange: (e) => { state.playing = e.data === YT.PlayerState.PLAYING; state.ended = e.data === YT.PlayerState.ENDED; } },
        });
        probeRef.current = () => {
          if (player?.getCurrentTime) { state.position = player.getCurrentTime(); state.duration = player.getDuration(); }
          return state;
        };
      });
      return () => player?.destroy?.();
    }
    if (info.kind === "vimeo") {
      player = new VimeoPlayer(box.current, { url: info.url, responsive: true });
      player.on("play", () => { state.playing = true; state.ended = false; });
      player.on("pause", () => { state.playing = false; });
      player.on("ended", () => { state.playing = false; state.ended = true; });
      player.on("timeupdate", (d) => { state.position = d.seconds; state.duration = d.duration; });
      return () => player.destroy();
    }
    const v = vid.current;
    probeRef.current = () => ({ playing: !v.paused && !v.ended && v.readyState > 2, ended: v.ended, position: v.currentTime, duration: v.duration || 0 });
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="video-player-container" className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:w-full [&_iframe]:h-full [&>div]:!p-0 [&>div]:h-full">
      {info.kind === "file" ? (
        <video ref={vid} data-testid="video-element" src={info.src} controls playsInline controlsList="nodownload" className="w-full h-full" />
      ) : (
        <div ref={box} className="absolute inset-0" />
      )}
    </div>
  );
};
